<script>
import { onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useGarageStore } from '@/stores/garage.js'
import { useModalStore } from '@/stores/modal.js'
import { useUIStore } from '@/stores/ui.js'
import { isVehicleCreated, formatMileageAsOf } from '@/lib/vehicle_helper.js';

export default {
  props: {
    vehicle: {
      type: Object,
      required: true,
    },
    hideOptions: {
      type: Boolean,
      default: false,
    }
  },
  emits: ['notify-toggle-expand'],
  setup(props, { emit }) {
    // Stores
    const garageStore = useGarageStore();
    const modalStore = useModalStore();
    const uiStore = useUIStore();

    // Variables
    const { t } = useI18n();
    const isLoading = ref(true);
    const mouseX = ref(0);
    const mouseY = ref(0);

    onMounted(() => {
      document.addEventListener('click', closeDropdownOnOutsideClick);
    });

    onUnmounted(() => {
      document.removeEventListener('click', closeDropdownOnOutsideClick);
    });

    const closeDropdownOnOutsideClick = (event) => {
      if (uiStore.isVehicleDropdownOpen(props.vehicle.id) && !event.target.closest('.vehicle-dropdown-menu')) {
        uiStore.closeVehicleDropdown();
      }
    };

    const toggleDropdown = (event) => {
      event.stopPropagation();
      mouseX.value = event.pageX;
      mouseY.value = event.pageY;
      
      if (uiStore.isVehicleDropdownOpen(props.vehicle.id)) {
        uiStore.closeVehicleDropdown();
      } else {
        uiStore.setVehicleDropdown(props.vehicle.id);
      }
    };

    const closeDropdown = () => {
      uiStore.closeVehicleDropdown();
    };

    const onFileChange = (event) => {
      garageStore.updateVehicleImage(event.target.files[0]);
      closeDropdown();
    };

    const emitToggleExpand = (vehicle, event) => {
      emit('notify-toggle-expand', vehicle, event);
    };

    return {
      props,
      t,
      isLoading,
      garageStore,
      modalStore,
      uiStore,
      emitToggleExpand,
      isVehicleCreated,
      formatMileageAsOf,
      mouseX,
      mouseY,
      toggleDropdown,
      closeDropdown,
      onFileChange,
    }
  },
};
</script>

<template>
  <div :key="props.vehicle.id" :class="`flex flex-row scrollable-card vehicle-card rounded-xl min-w-full sm:min-w-[450px] max-w-[450px] min-h-[100px] sm:min-h-[155px] relative ${garageStore.currentVehicle && props.vehicle.id === garageStore.currentVehicle.id ? 'active-vehicle shadow-xl' : ''} cursor-pointer transition ease-in-out duration-200`" @click="garageStore.setCurrentVehicle(vehicle)">
    <img v-if="props.vehicle.image_url" :src="props.vehicle.image_url" class="h-[100px] sm:h-[155px] w-2/5 object-cover rounded-l-xl" />
    <div v-else class="no-vehicle-container h-[100px] sm:h-[155px] w-2/5 flex justify-center items-center rounded-l-xl">
      <!-- <img src="../../assets/icons/directions_car_40dp_FILL1_wght400_GRAD0_opsz40.svg" class="h-full p-4 object-cover"> -->
      <img src="../../assets/images/aw1.png" alt="Image 1" class="h-full p-2 object-cover">
    </div>
    <div class="pl-3 sm:pl-6 pt-2 sm:pt-4 w-3/5">
      <div v-if="isVehicleCreated(vehicle, t)" class="flex h-[40px] sm:h-[56px] mb-1 pr-3 sm:pr-6">
        <p class="font-semibold text-sm sm:text-lg line-clamp-2 max-h-[40px] sm:max-h-[56px] my-auto">
          {{ props.vehicle.full_vehicle_name }}
        </p>
      </div>
      <p v-else class="font-semibold text-sm sm:text-lg mt-1 sm:mt-2 mb-1">
        {{ t('my_garage.unassigned_vehicle') }}
      </p>
      <p v-if="isVehicleCreated(vehicle, t)" class="text-xs sm:text-sm mileage-as-of mr-2 line-clamp-1 hidden sm:block">
        {{ t('my_garage.mileage_as_of', { date: formatMileageAsOf(props.vehicle.mileage_updated_at) }) }}
      </p>
      <p v-if="isVehicleCreated(vehicle, t) && props.vehicle.mileage" :class="`font-semibold text-sm sm:text-lg pb-1 mr-2 ${!hideOptions ? 'border-b' : ''}`">
        {{ props.vehicle.mileage.toLocaleString('en-US') }} {{ t('my_garage.miles') }}
      </p>
      <p v-else-if="isVehicleCreated(vehicle, t)" class="font-semibold text-sm sm:text-lg pb-1 mr-2">
        {{ t('my_garage.no_mileage') }}
      </p>
      <!-- <p v-if="!isVehicleCreated(vehicle, t)" class="text-sm mileage-as-of mr-2 font-medium mb-2">
        {{ t('my_garage.unassigned_vehicle_text') }}
      </p> -->
      <div v-if="!isVehicleCreated(vehicle, t)" class="flex flex-row h-full w-full items-center pb-4">
        <div  class="flex items-center justify-center px-2 rounded-full font-regular add-vehicle cursor-pointer hover:opacity-90 h-full w-full pr-6" @click="modalStore.setModal(modalStore.modalTypeConstants.ADD_VEHICLE)">
          <span class="px-1 focus:outline-none">
           {{ t('my_garage.add_a_vehicle') }}
          </span>
          <img src="../../assets/icons/add_circle_24dp_FILL0_wght400_GRAD0_opsz24.svg" alt="Logo 1" class="h-[22px] w-[22px] pr-1">
        </div>
      </div>
      <div v-if="isVehicleCreated(vehicle, t) && !hideOptions" 
           class="absolute bottom-0 right-2 p-0.5 cursor-pointer hover:bg-gray-200 rounded hidden sm:block"
           @click.stop="toggleDropdown">
        <img :class="`expand-dropdown-${props.vehicle.id}`" 
             src="../../assets/icons/more_horiz_24dp_FILL0_wght400_GRAD0_opsz24.svg"
             class="w-6 h-6">
      </div>
    </div>

    <!-- Dropdown Menu -->
    <transition enter-active-class="transition ease-out duration-100"
                leave-active-class="transition ease-in duration-75">
      <div v-if="uiStore.isVehicleDropdownOpen(props.vehicle.id)"
           :style="{ top: `${mouseY}px`, left: `calc(${mouseX}px - 12.5rem)` }"
           class="fixed z-50 mt-2 w-48 bg-white rounded-md shadow-lg vehicle-dropdown-menu">
        <ul class="w-48 bg-white rounded-md shadow-lg overflow-hidden" @click="closeDropdown">
          <li class="block px-4 py-2 text-sm font-semibold text-gray-700 cursor-pointer" 
              @click="modalStore.setModal(modalStore.modalTypeConstants.EDIT_VEHICLE, vehicle)">
            {{ t('my_garage.edit_vehicle') }}
          </li>
          <label class="block px-4 py-2 cursor-pointer">
            <input type="file" @change="onFileChange" accept="image/*" class="hidden">
            <span class="text-sm font-semibold text-gray-700">{{ t('my_garage.vehicle_info_section.vehicle_image_upload') }}</span>
          </label>
          <li class="block px-4 py-2 text-sm font-semibold text-gray-700 cursor-pointer" 
              @click="garageStore.archiveVehicle(vehicle)">
            {{ t('my_garage.hide_vehicle') }}
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/views/my_garage/my_garage';
</style>